/**
 * Minimal RFC 4180 CSV parser.
 *
 * The intake pipeline ingests client-emailed CSV feeds (see client-feeds.ts).
 * Those files are small (tens–hundreds of rows), so we parse the whole text
 * in memory — no streaming, no dependency. Handles the full quoting grammar:
 *
 *   - quoted fields ("a,b")
 *   - embedded commas, CR/LF newlines and doubled quotes ("she said ""hi""")
 *   - both \r\n and \n record separators
 *   - a trailing newline does not produce a phantom empty record
 *
 * Deliberately NOT handled: alternate delimiters (the six feeds are all
 * comma-separated) and charset sniffing (callers hand us a decoded string).
 */
/** Parse CSV text into rows of raw string cells. */
export function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    /** True once the current record has any content (field chars or a comma). */
    let recordStarted = false;
    const pushField = () => {
        row.push(field);
        field = '';
    };
    const pushRow = () => {
        pushField();
        rows.push(row);
        row = [];
        recordStarted = false;
    };
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i += 1; // consume the escaped quote's second char
                }
                else {
                    inQuotes = false;
                }
            }
            else {
                field += ch;
            }
            continue;
        }
        if (ch === '"') {
            inQuotes = true;
            recordStarted = true;
            continue;
        }
        if (ch === ',') {
            pushField();
            recordStarted = true;
            continue;
        }
        if (ch === '\r') {
            // Swallow the \r of a \r\n pair; a bare \r also terminates the record.
            if (recordStarted || field.length > 0)
                pushRow();
            if (text[i + 1] === '\n')
                i += 1;
            continue;
        }
        if (ch === '\n') {
            if (recordStarted || field.length > 0)
                pushRow();
            continue;
        }
        field += ch;
        recordStarted = true;
    }
    // Flush the final record when the file doesn't end with a newline.
    if (recordStarted || field.length > 0)
        pushRow();
    return rows;
}
/**
 * Parse CSV text and key each data row by the (trimmed) header row.
 * Returns undefined when the text has no header row at all.
 */
export function parseCsvTable(text) {
    const rows = parseCsv(text);
    const headerRow = rows[0];
    if (!headerRow)
        return undefined;
    const headers = headerRow.map((h) => h.trim());
    const records = rows.slice(1).map((cells) => {
        const record = {};
        headers.forEach((h, idx) => {
            record[h] = (cells[idx] ?? '').trim();
        });
        return record;
    });
    return { headers, records };
}
//# sourceMappingURL=csv.js.map